/* eslint-disable @typescript-eslint/no-use-before-define */
import { StatusColors } from '@euler/components/Colors';
import { Img } from '@euler/components/image/AdvancedImage';
import Euler from '@euler/components/layout';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { InspectionTaskStatus, ReceptionOrderStatus } from '@euler/model/enum';
import { defaultFormatDateTime } from '@euler/utils/formatters';
import { displayChassisNo } from '@euler/utils/vin/displayChassisNo';
import { memo } from 'react';
import { Image } from 'react-native';
import { OrderInfoSection } from '../OrderInfoSection';
import { CustomerInfoRow } from './CustomerInfoRow';
import { CustomerPhoneInfoRow } from './CustomerPhoneInfoRow';
import { ExtendedPropsInfo } from './ExtendedPropsInfo';
import { OrderSummaryHeaderRightView } from './HeaderRightView';
import { InlineOrderInfoCells } from './InlineOrderInfoCells';
import { OrderSubscriptionButton } from './OrderSubscriptionButton';
import { OrderTagsInfoRow } from './OrderTagsInfoRow';
import { ServiceAgentInfoRow } from './ServiceAgentInfoRow';
import { OrderSummaryInfoRow } from './SummaryInfoRow';
import { OrderSummaryTitleDecoratorView } from './TitleDecoratorView';
import { OrderSummaryCardProps } from './types';
import { kCustomerSignedSummaryItemKey, useOrderSummary } from './util';

export const OrderSummaryCard = memo((props: OrderSummaryCardProps) => {
  const { rpx } = useResponsiveContext();
  const { isTablet } = useSystemMetrics();
  const {
    $visibility,
    $summaryVisibility,
    extendedPropItems,
    userPermissions,
  } = useOrderSummary(props);

  const hasCustomerSignSummaryItem =
    extendedPropItems.find(x => x.id === kCustomerSignedSummaryItemKey) != null;

  return (
    <OrderInfoSection
      noBodyPadding
      title={props.hasChildOrders ? '接车信息 ⛁' : '接车信息'}
      style={props.style}
      headerStyle={props.headerStyle}
      titleTextStyle={props.titleTextStyle}
      onHeaderRightPress={props.onEditPress}
      titleDecoratorView={
        <>
          <OrderSummaryTitleDecoratorView
            status={props.status as unknown as ReceptionOrderStatus}
            isFirstTimeOrder={props.isFirstTimeOrder}
            isRollback={props.isRollback}
          />
        </>
      }
      headerRight={
        <OrderSummaryHeaderRightView
          status={props.status}
          onShowDetail={props.onShowDetail}
          onEditPress={props.onEditPress}
        />
      }
    >
      <Euler.View
        paddingHorizontal={rpx(15)}
        paddingVertical={rpx(15)}
        mr={props.brandLogoPosition === 'right' ? 0 : rpx(11)}
      >
        <Img
          pointerEvents="none"
          uri={props.vehicleBrandLogo}
          width={rpx(isTablet ? 80 : 40)}
          height={rpx(isTablet ? 80 : 40)}
          {...(props.brandLogoPosition === 'right'
            ? {
                position: 'absolute',
                right: rpx(15),
                top: rpx(8),
              }
            : {})}
        />

        <OrderSummaryInfoRow label="订单编号" value={props.orderNo} copyable />

        <OrderSummaryInfoRow
          label="记录编号"
          value={props.serviceRecordId}
          defaultVisible={props.serviceRecordId != null}
          copyable
        />

        <OrderSummaryInfoRow
          label="工单编号"
          value={props.taskNo}
          defaultVisible={props.taskNo != null}
          copyable
        />

        <OrderSummaryInfoRow
          label="车牌号码"
          value={kAppStoreSnapshotMode ? '沪A55555' : props.licensePlateNo}
          visibility={$visibility('licensePlateNo')}
          defaultVisible={Boolean(props.licensePlateNo)}
          copyable
        />

        <OrderSummaryInfoRow
          label="车架号码"
          value={
            kAppStoreSnapshotMode
              ? 'AAAAABB11111100000'
              : displayChassisNo(props)
          }
          visibility={$visibility('vin')}
          defaultVisible={Boolean(displayChassisNo(props))}
          copyable
        />

        <OrderSummaryInfoRow
          label="进店时间"
          value={props.createdAt}
          format={defaultFormatDateTime}
        />

        {props.estimatedFinishTime != null &&
        props.status === InspectionTaskStatus.Finished ? (
          <OrderSummaryInfoRow
            label="预计完工"
            value={props.estimatedFinishTime}
            format={defaultFormatDateTime}
          />
        ) : null}

        <OrderSummaryInfoRow
          label="完工时间"
          value={props.finishedAt}
          format={defaultFormatDateTime}
        />

        <OrderSummaryInfoRow
          label="服务门店"
          value={props.storeName}
          visibility={$summaryVisibility('storeName')}
        />

        <CustomerInfoRow
          customerName={props.customerName}
          customerMobile={props.customerMobile}
          customerType={props.customerType}
          returnType={props.returnType}
          customerNameVisibility={$visibility('customerName')}
          customerTypeVisibility={$visibility('customerType')}
          returnTypeVisibility={$visibility('returnType')}
          customerMobileVisibility={
            userPermissions?.dialCustomer
              ? 'none'
              : $visibility('customerMobile')
          }
          defaultVisible={!props.hideCustomerInfo}
        />

        <CustomerPhoneInfoRow
          value={props.customerMobile}
          canDialCustomer={userPermissions?.dialCustomer}
          visibility={
            props.hideCustomerInfo || !userPermissions?.dialCustomer
              ? 'none'
              : undefined
          }
        />

        <OrderTagsInfoRow
          value={props.tags}
          visibility={$visibility('tags') ?? 'always'}
        />

        <OrderSummaryInfoRow
          label="返单时间"
          value={props.rollbackAt}
          format={defaultFormatDateTime}
          labelStyle={{ color: StatusColors.Danger }}
          textStyle={{ color: StatusColors.Danger }}
        />

        <OrderSummaryInfoRow
          label="返单原因"
          value={props.rollbackReason}
          labelStyle={{ color: StatusColors.Danger }}
          textStyle={{ color: StatusColors.Danger }}
          mb={rpx(9)}
        />

        {props.status === InspectionTaskStatus.Cancelled &&
        props.canceledAt != null ? (
          <>
            <OrderSummaryInfoRow
              label="闭单时间"
              visibility="always"
              value={props.canceledAt}
              format={defaultFormatDateTime}
              labelStyle={{ color: StatusColors.Warn }}
              textStyle={{ color: StatusColors.Warn }}
              mb={rpx(9)}
            />
            {props.cancelReason?.trim() ? (
              <OrderSummaryInfoRow
                label="闭单原因"
                visibility="always"
                value={props.cancelReason}
                labelStyle={{ color: StatusColors.Warn }}
                textStyle={{ color: StatusColors.Warn }}
                mb={rpx(9)}
              />
            ) : null}
          </>
        ) : null}

        <ServiceAgentInfoRow
          isMainOrder={props.isMainOrder}
          serviceAgentName={props.serviceAgentName}
          serviceAgentMemberId={props.serviceAgentMemberId}
          serviceAgentChatByName={props.serviceAgentChatByName}
          createdAt={props.createdAt}
          finishedAt={props.finishedAt}
          status={props.status}
          estimatedFinishTime={props.estimatedFinishTime}
          estimatedFinishTimeVisibility={$visibility('estimatedFinishTime')}
        />

        {props.status === InspectionTaskStatus.Cancelled ? (
          <Image
            source={require('./order-closed.png')}
            css={`
              position: absolute;
              right: ${rpx(10)}px;
              bottom: ${rpx(30)}px;
              width: ${rpx(80)}px;
              height: ${rpx(80)}px;
            `}
          />
        ) : null}
      </Euler.View>

      <InlineOrderInfoCells
        licensePlateNo={props.licensePlateNo}
        vin={props.vin}
        vehicleMileage={props.vehicleMileage}
        vehicleFuelType={props.vehicleFuelType}
        serviceRecordCount={props.serviceRecordCount}
        hideServiceRecordCount={props.hideServiceRecordCount}
        customerSigned={props.customerSigned}
        licensePlateNoVisibility={$visibility('licensePlateNo')}
        vehicleMileageVisibility={$visibility('mileage') ?? 'always'}
        vehicleFuelTypeVisibility={
          $summaryVisibility('vehicleFuelType') ?? 'always'
        }
        customerSignedVisibility={
          hasCustomerSignSummaryItem
            ? 'none'
            : $summaryVisibility('customerSigned')
        }
        onServiceRecordsPress={props.onServiceRecordsPress}
        mt={rpx(2)}
        mb={rpx(15)}
      />

      <ExtendedPropsInfo items={extendedPropItems} />

      <OrderSubscriptionButton
        subscribed={props.subscribed ?? false}
        disabled={
          props.qrcodeSharingDisabled ||
          props.status === InspectionTaskStatus.Cancelled
        }
        onPress={props.onQrcodeSharingPress}
        style={
          props.status === InspectionTaskStatus.Cancelled
            ? { backgroundColor: '#aaa' }
            : undefined
        }
      />
    </OrderInfoSection>
  );
});
